import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Image,
  Card,
  Text,
  Spinner,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Button,
  Switch,
  VStack,
  HStack,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tag,
  TagLabel,
  Link,
  Grid,
  GridItem,
} from "@chakra-ui/react";

import { BackGroundUI } from "../../components";

import LogoImage from "../../assets/logo.webp";
import ConFetti from "../../assets/confetti.svg";

import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ManageItemService } from "../../services";

import styles from "./index.module.css";

export default function ManageItem() {
  const { itemId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [itemData, setItemData] = useState(null);
  const [canEdit, setCanEdit] = useState(false);

  // Form state
  const [itemName, setItemName] = useState("");
  const [isActivated, setIsActivated] = useState(true);

  // Initial values for change detection
  const [initialItemName, setInitialItemName] = useState("");
  const [initialIsActivated, setInitialIsActivated] = useState(true);

  // Check if there are unsaved changes
  const hasChanges = itemName !== initialItemName || isActivated !== initialIsActivated;

  // Alert history state
  const [alertHistory, setAlertHistory] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  useEffect(() => {
    const validateAndLoad = async () => {
      if (!token || !itemId) {
        setError("Missing token or item ID. Please use the link from your email.");
        setLoading(false);
        return;
      }

      try {
        const result = await ManageItemService.validateAndGetItem(itemId, token);

        if (result.valid) {
          setItemData(result.item);
          setCanEdit(result.canEdit);

          // Set form values
          const name = result.item.productInfo?.name || "";
          const activated = result.item.isActivated !== false;
          setItemName(name);
          setIsActivated(activated);
          // Store initial values for change detection
          setInitialItemName(name);
          setInitialIsActivated(activated);
        } else {
          setError("Invalid or expired token. Please request a new link.");
        }
      } catch (err) {
        console.error("Error validating token:", err);
        setError(err.message || "Failed to validate token. It may have expired.");
      } finally {
        setLoading(false);
      }
    };

    validateAndLoad();
  }, [token, itemId]);

  // Load alert history after item data is loaded
  useEffect(() => {
    const loadAlertHistory = async () => {
      if (!itemData || !token || !itemId) return;

      setLoadingAlerts(true);
      try {
        const result = await ManageItemService.getAlertHistory(itemId, token);
        setAlertHistory(result.alerts || []);
      } catch (err) {
        console.error("Error loading alert history:", err);
      } finally {
        setLoadingAlerts(false);
      }
    };

    loadAlertHistory();
  }, [itemData, token, itemId]);

  const handleSave = async () => {
    if (!canEdit) {
      toast({
        title: "Cannot edit",
        description: "This token does not have edit permissions.",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setSaving(true);
    try {
      await ManageItemService.updateItem(itemId, token, {
        name: itemName,
        isActivated: isActivated,
      });

      // Update initial values to reflect saved state
      setInitialItemName(itemName);
      setInitialIsActivated(isActivated);

      toast({
        title: "Item updated successfully!",
        description: "Your changes have been saved.",
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      console.error("Error updating item:", err);
      toast({
        title: "Error updating item",
        description: err.message || "Please try again.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "N/A";
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatAlertDateTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    const hour12 = hours % 12 || 12;
    const suffix = day === 1 || day === 21 || day === 31 ? "st" :
                   day === 2 || day === 22 ? "nd" :
                   day === 3 || day === 23 ? "rd" : "th";
    return `${day}${suffix} ${month} @ ${hour12}:${minutes}${ampm}`;
  };

  const calculateDiscount = (originalPrice, scrapedPrice) => {
    if (!originalPrice || !scrapedPrice) return null;
    const discount = Math.round(((originalPrice - scrapedPrice) / originalPrice) * 100);
    if (discount === 0) return null;
    return discount > 0 ? `-${discount}%` : `+${Math.abs(discount)}%`;
  };

  // Loading state
  if (loading) {
    return (
      <Box bg="purchr.bgHero" minH="100vh" p={{ base: "0", sm: "0px", xl: "0 40px" }}>
        <BackGroundUI />
        <Flex h="100vh" justifyContent="center" alignItems="center">
          <Card p="48px" borderRadius="42px" textAlign="center">
            <Spinner size="xl" color="purchr.blue.dark" mb={4} />
            <Text>Loading item details...</Text>
          </Card>
        </Flex>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box bg="purchr.bgHero" minH="100vh" p={{ base: "0", sm: "0px", xl: "0 40px" }}>
        <BackGroundUI />
        <Flex h="100vh" justifyContent="center" alignItems="center">
          <Card p="48px" borderRadius="42px" maxW="500px">
            <VStack spacing={4}>
              <Image src={LogoImage} height="60px" />
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Unable to load item</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Box>
              </Alert>
              <Button
                colorScheme="blue"
                onClick={() => navigate("/login")}
              >
                Go to Login
              </Button>
            </VStack>
          </Card>
        </Flex>
      </Box>
    );
  }

  // Main form
  return (
    <Box bg="purchr.bgHero" minH="100vh" p={{ base: "16px", sm: "24px", xl: "40px" }}>
      <BackGroundUI />
      <Flex justifyContent="center" pt={{ base: "20px", md: "40px" }}>
        <Card
          p={{ base: "24px", sm: "48px" }}
          borderRadius="24px"
          maxW="600px"
          w="100%"
          className={styles.card}
        >
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <Flex justifyContent="space-between" alignItems="center">
              <Image src={LogoImage} height="40px" />
              <HStack spacing={3}>
                <Badge colorScheme={isActivated ? "green" : "gray"} fontSize="sm" px={3} py={1}>
                  {isActivated ? "Active" : "Paused"}
                </Badge>
                <Switch
                  isChecked={isActivated}
                  onChange={(e) => setIsActivated(e.target.checked)}
                  colorScheme="green"
                  size="lg"
                  isDisabled={!canEdit}
                />
              </HStack>
            </Flex>

            <Divider />

            {/* Item Preview */}
            <HStack spacing={4} align="start">
              {itemData?.productInfo?.thumbnailURL && (
                <Image
                  src={itemData.productInfo.thumbnailURL}
                  alt={itemData.productInfo.name}
                  boxSize="100px"
                  objectFit="contain"
                  borderRadius="md"
                  bg="white"
                  p={2}
                />
              )}
              <VStack align="start" spacing={1} flex={1}>
                <HStack spacing={2}>
                  {itemData?.productInfo?.retailerIcon && (
                    <Image
                      src={itemData.productInfo.retailerIcon}
                      alt={itemData.productInfo.retailer}
                      boxSize="20px"
                      objectFit="contain"
                    />
                  )}
                  <Text fontSize="sm" color="gray.500">
                    {itemData?.productInfo?.retailer || "Unknown retailer"}
                  </Text>
                </HStack>
                <Link
                  href={itemData?.productInfo?.URL}
                  fontWeight="bold"
                  fontSize="lg"
                  noOfLines={2}
                  color="purchr.blue.dark"
                  isExternal
                >
                  {itemData?.productInfo?.name || "Untitled Item"}
                </Link>
                <HStack spacing={2} flexWrap="wrap" alignItems="center">
                  <Box
                    bg="purchr.orange.light"
                    color="white"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    <Text fontSize="lg" fontWeight="bold">
                      {formatPrice(itemData?.lastAlertPrice)}
                    </Text>
                  </Box>
                  {itemData?.lastAlertTime && (
                    <Text fontSize="xs" color="gray.400">
                      ({formatDate(itemData.lastAlertTime)})
                    </Text>
                  )}
                  {itemData?.originalPrice && (
                    <Text fontSize="sm" color="gray.400" textDecoration="line-through">
                      {formatPrice(itemData.originalPrice)}
                    </Text>
                  )}
                </HStack>
              </VStack>
            </HStack>

            <Divider />

            {/* Edit Form */}
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Item Name</FormLabel>
                <Input
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="Enter a custom name for this item"
                  isDisabled={!canEdit}
                />
              </FormControl>
            </VStack>

            {/* Alert History */}
            <Box>
              <Text fontWeight="bold" fontSize="md" mb={3}>
                Price Alert History
              </Text>
              {loadingAlerts ? (
                <Flex justify="center" py={4}>
                  <Spinner size="sm" />
                </Flex>
              ) : alertHistory.length === 0 ? (
                <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                  No price alerts yet for this item.
                </Text>
              ) : (
                <VStack spacing={2} align="stretch" maxH="300px" overflowY="auto">
                  {alertHistory.map((alert, index) => (
                    <Grid
                      key={alert._id || index}
                      bg="gray.50"
                      templateColumns={{ base: "1fr", md: "120px 1fr auto" }}
                      alignItems="center"
                      borderRadius="8px"
                      gap={2}
                      p={3}
                    >
                      {/* Date/Time */}
                      <GridItem display={{ base: "none", md: "block" }}>
                        <Text fontSize="xs" color="gray.500">
                          {formatAlertDateTime(alert.scrapedAt)}
                        </Text>
                      </GridItem>

                      {/* Product info (mobile shows date here) */}
                      <GridItem>
                        <Link
                          href={alert.URL}
                          color="purchr.blue.dark"
                          fontSize="sm"
                          fontWeight="600"
                          noOfLines={1}
                          isExternal
                        >
                          {alert.name || itemData?.productInfo?.name || "Item"}
                        </Link>
                        <Text
                          fontSize="xs"
                          color="gray.400"
                          display={{ base: "block", md: "none" }}
                          mt={1}
                        >
                          {formatAlertDateTime(alert.scrapedAt)}
                        </Text>
                      </GridItem>

                      {/* Price with discount */}
                      <GridItem>
                        <Tag
                          p={2}
                          bg="purchr.orange.light"
                          color="white"
                          borderRadius="4px"
                          gap={1}
                        >
                          <Image src={ConFetti} w="14px" h="14px" />
                          <TagLabel display="flex" gap={1} fontSize="sm">
                            <Text fontWeight="600">
                              ${parseFloat(alert.scrapedPrice).toFixed(2)}
                            </Text>
                            {calculateDiscount(alert.originalPrice, alert.scrapedPrice) && (
                              <Text fontWeight="400">
                                ({calculateDiscount(alert.originalPrice, alert.scrapedPrice)})
                              </Text>
                            )}
                          </TagLabel>
                        </Tag>
                      </GridItem>
                    </Grid>
                  ))}
                </VStack>
              )}
            </Box>

            <Divider />

            {/* Actions */}
            <HStack spacing={4} justify="flex-end">
              <Button
                variant="ghost"
                onClick={() => window.close()}
              >
                Cancel
              </Button>
              <Button
                colorScheme={hasChanges ? "blue" : "gray"}
                onClick={handleSave}
                isLoading={saving}
                loadingText="Saving..."
                isDisabled={!canEdit || !hasChanges}
              >
                Save Changes
              </Button>
            </HStack>
          </VStack>
        </Card>
      </Flex>
    </Box>
  );
}
